import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  ShieldCheck, Star, ExternalLink, CheckCircle2, Search, AlertCircle, Award, Scale,
  MessageSquareWarning, Gauge, Plus, Check, Loader2, ClipboardList, Info, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_STYLE = {
  VERIFIED_LISTING: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  LISTING_FOUND: { cls: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle2 },
  LIKELY_MATCH: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Search },
  LISTING_NOT_FOUND: { cls: "bg-slate-100 text-slate-500 border-slate-200", icon: Search },
  ACCESS_NOT_CONFIGURED: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle },
  ACCESS_RESTRICTED: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertCircle },
  SOURCE_UNAVAILABLE: { cls: "bg-orange-50 text-orange-700 border-orange-200", icon: AlertCircle },
  NO_DATA: { cls: "bg-slate-100 text-slate-500 border-slate-200", icon: AlertCircle },
};

const BAND_COLOR = {
  Excellent: "text-emerald-600", Strong: "text-emerald-600", Fair: "text-amber-600",
  "Needs attention": "text-orange-600", Poor: "text-red-600",
};

function StatusPill({ status, label }) {
  const st = STATUS_STYLE[status] || STATUS_STYLE.LISTING_NOT_FOUND;
  const Icon = st.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>
      <Icon className="w-3 h-3" /> {label || status}
    </span>
  );
}

function HealthScore({ hs }) {
  if (!hs) return null;
  if (!hs.available) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="reputation-health-insufficient">
        <p className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Gauge className="w-4 h-4 text-slate-400" /> Reputation Health Score</p>
        <p className="mt-1 text-slate-900 font-heading text-lg">{hs.message}</p>
        <p className="text-xs text-slate-500 mt-1">{hs.explanation}</p>
      </div>
    );
  }
  const color = BAND_COLOR[hs.band] || "text-slate-700";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="reputation-health-score">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> Reputation Health Score</p>
          <p className="text-[11px] text-slate-400">Calculated · {hs.model_version}</p>
        </div>
        <div className="text-right">
          <p className={`font-heading text-4xl font-bold ${color}`}>{hs.score}<span className="text-lg text-slate-300">/100</span></p>
          <p className={`text-sm font-semibold ${color}`}>{hs.band}</p>
        </div>
      </div>
      {hs.confidence === "Low" && <p className="mt-1 text-[11px] text-amber-600">{hs.low_confidence_reason} Treat as indicative.</p>}
      <div className="mt-3 space-y-1.5">
        {(hs.components || []).filter((c) => c.included).map((c) => (
          <div key={c.factor} className="flex items-center justify-between text-xs">
            <span className="text-slate-500 capitalize">{c.factor.replace(/_/g, " ")}: <span className="text-slate-700">{c.value}</span></span>
            <span className="text-slate-400">+{c.contribution}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-slate-400">{hs.formula}</p>
    </div>
  );
}

function BbbCard({ bbb, business }) {
  const [open, setOpen] = useState(false);
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  if (!bbb) return null;

  const openPrep = async () => {
    setOpen(true);
    if (prep) return;
    setLoading(true);
    try {
      const { data } = await api.post("/reputation/bbb/preparation", {
        name: business?.name, city: business?.city, state: business?.state, category: business?.category,
        website_url: business?.website_url,
      });
      setPrep(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const Field = ({ label, icon: Icon, data }) => (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className="text-sm font-semibold text-slate-700 mt-0.5">{data.value ?? "Not available via API"}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{data.note}</p>
    </div>
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="reputation-bbb">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#005a9c] text-white font-bold text-xs">BBB</span>
          <div>
            <p className="font-heading font-semibold text-slate-900">Better Business Bureau</p>
            <StatusPill status={bbb.listing_status} label={bbb.listing_status_label} />
          </div>
        </div>
        {bbb.profile_url ? (
          <a href={bbb.profile_url} target="_blank" rel="noopener noreferrer" data-testid="bbb-profile-link"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-900">
            View profile <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : (
          <a href={bbb.official_search_url} target="_blank" rel="noopener noreferrer" data-testid="bbb-search-link"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-900">
            Official BBB search <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      {bbb.identity_match && bbb.identity_match !== "none" && (
        <p className="mt-2 text-[12px] text-slate-500">Identity match: <span className="font-semibold capitalize">{bbb.identity_match}</span>{bbb.match_confidence ? ` (${Math.round(bbb.match_confidence * 100)}% confidence — confirm it's your business)` : ""}</p>
      )}
      {bbb.message && <p className="mt-2 text-[12px] text-slate-600">{bbb.message}</p>}

      <div className="mt-3 grid sm:grid-cols-3 gap-2">
        <Field label="BBB Rating" icon={Award} data={bbb.bbb_rating} />
        <Field label="Accreditation" icon={ShieldCheck} data={bbb.accreditation} />
        <Field label="Complaints" icon={MessageSquareWarning} data={bbb.complaints} />
      </div>
      <p className="mt-2 text-[11px] text-slate-400 flex items-start gap-1.5"><Scale className="w-3.5 h-3.5 mt-0.5 shrink-0" /> A BBB letter rating is <b className="mx-1">not</b> the same as BBB Accreditation — they are separate and both verified on bbb.org.</p>

      <Button size="sm" variant="outline" onClick={openPrep} data-testid="bbb-prep-btn"
        className="mt-3 text-[#005a9c] border-blue-200 hover:bg-blue-50">
        <ClipboardList className="w-4 h-4 mr-1.5" /> Profile preparation help
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="bbb-prep-dialog">
          <DialogHeader><DialogTitle>BBB profile preparation</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Guided steps to prepare accurate information for a Better Business Bureau profile. Revenue Radar does not submit, register or accredit your business.</DialogDescription>
          {loading ? <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : prep && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">{prep.disclaimer}</p>
              <ol className="space-y-2">
                {(prep.steps || []).map((s) => (
                  <li key={s.step} className="rounded-lg border border-slate-200 p-3" data-testid={`bbb-prep-step-${s.step}`}>
                    <p className="text-sm font-semibold text-slate-800">{s.step}. {s.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{s.detail}</p>
                    {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-[12px] text-[#005a9c] hover:underline inline-flex items-center gap-1 mt-1">Open official resource <ExternalLink className="w-3 h-3" /></a>}
                    {s.fields_needed && <p className="text-[11px] text-amber-600 mt-1">Prepare: {s.fields_needed.join(", ")}</p>}
                  </li>
                ))}
              </ol>
              {prep.service && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="bbb-prep-service">
                  <p className="text-sm font-semibold text-slate-900">{prep.service.name}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{prep.service.client_facing_description || prep.service.recommended_solution}</p>
                  {prep.service.pricing?.client_price != null && (
                    <p className="text-sm font-bold text-slate-900 mt-1">From ${prep.service.pricing.client_price.toLocaleString()}</p>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1">An existing Revenue Radar service — this is not an official BBB service and does not guarantee accreditation.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ReputationIntelligence({ data, business, onFix, added = {} }) {
  const [showAll, setShowAll] = useState(false);
  if (!data || !data.enabled) return null;
  const s = data.summary || {};
  const verified = data.verified_platforms || [];

  // Display reconciliation: a listing whose platform has a VERIFIED rating should never show
  // "No listing found". Mirrors the backend; keeps old/stored reports visually consistent.
  const alias = { google_places: "google_business_profile" };
  const vpById = {};
  verified.forEach((p) => {
    if (p.rating == null) return;
    vpById[p.platform_id] = p;
    if (alias[p.platform_id]) vpById[alias[p.platform_id]] = p;
  });
  const listings = (data.listings || []).map((l) => {
    const vp = vpById[l.id];
    if (vp && vp.rating != null && ["LISTING_NOT_FOUND", "ACCESS_NOT_CONFIGURED", null, undefined].includes(l.listing_status)) {
      return { ...l, listing_status: "VERIFIED_LISTING", listing_status_label: "Verified listing",
               metrics: { ...(l.metrics || {}), rating: vp.rating, review_count: vp.review_count },
               matched_url: l.matched_url || vp.source_url };
    }
    return l;
  });
  const shown = showAll ? listings : listings.slice(0, 6);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="reputation-intelligence">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-violet-600 text-white"><ShieldCheck className="w-5 h-5" /></span>
        <div>
          <h3 className="font-heading text-xl font-bold text-slate-900">Reputation Intelligence</h3>
          <p className="text-[12px] text-slate-500">Verified reviews, listings & BBB · v{data.module_version}</p>
        </div>
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-4">
        <HealthScore hs={data.health_score} />
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="reputation-verified">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1.5 mb-2"><Star className="w-3.5 h-3.5" /> Verified platform ratings</p>
          {verified.length ? verified.map((p) => (
            <div key={p.platform_id} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                {p.source_url ? <a href={p.source_url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1">{p.platform} <ExternalLink className="w-3 h-3 text-slate-400" /></a> : p.platform}
                <span className="text-[10px] font-bold uppercase text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Verified</span>
              </span>
              <span className="text-sm font-semibold text-slate-900">★ {p.rating}{p.review_count != null ? <span className="text-slate-400 font-normal"> · {p.review_count} reviews</span> : ""}</span>
            </div>
          )) : <p className="text-sm text-slate-500">No verified platform ratings were retrieved. Missing data is never scored as poor.</p>}
        </div>
      </div>

      <div className="mt-5"><BbbCard bbb={data.bbb} business={business} /></div>

      {listings.length > 0 && (
        <div className="mt-5">
          <p className="text-sm font-semibold text-slate-700 mb-2">Review & reputation listings</p>
          <div className="space-y-2.5">
            {shown.map((src, i) => (
              <div key={src.id} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-3.5" data-testid={`reputation-listing-${i}`}>
                <div className="min-w-0">
                  <span className="font-semibold text-slate-900">{src.platform}</span>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <StatusPill status={src.listing_status} label={src.listing_status_label} />
                    {src.metrics?.rating != null && <span className="text-[12px] text-slate-600 font-medium">★ {src.metrics.rating}{src.metrics.review_count != null ? ` · ${src.metrics.review_count} reviews` : ""}</span>}
                  </div>
                </div>
                {(src.verify_url || src.matched_url) && (
                  <a href={src.matched_url || src.verify_url} target="_blank" rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:text-slate-900">
                    {src.matched_url ? "View" : "Check"} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
          {listings.length > 6 && (
            <button onClick={() => setShowAll((v) => !v)} data-testid="reputation-listings-toggle"
              className="mt-3 text-[13px] font-medium text-violet-600 hover:text-violet-700">
              {showAll ? "Show fewer" : `Show all ${listings.length} listings`}
            </button>
          )}
        </div>
      )}

      {(data.recommendations || []).length > 0 && (
        <div className="mt-5" data-testid="reputation-recommendations">
          <p className="text-sm font-semibold text-slate-700 mb-2">Recommended improvements</p>
          <div className="space-y-2.5">
            {data.recommendations.map((rec, i) => {
              const f = (data.findings || [])[i] || {};
              const key = f.code || rec.finding;
              const done = !!added[key];
              return (
                <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`reputation-rec-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-sm">{rec.finding}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{rec.recommended_action}</p>
                      <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                        <span className="rounded bg-slate-100 text-slate-600 px-1.5 py-0.5 font-medium">{rec.priority} priority</span>
                        <span className="rounded bg-slate-100 text-slate-600 px-1.5 py-0.5 font-medium">{rec.confidence} confidence</span>
                      </div>
                      {rec.evidence && <p className="text-[11px] text-slate-400 mt-1">Evidence: {rec.evidence}</p>}
                    </div>
                    {onFix && (
                      <Button size="sm" onClick={() => onFix(f)} disabled={done} data-testid={`reputation-fix-${i}`}
                        className={done ? "bg-emerald-600 shrink-0" : "bg-slate-900 hover:bg-slate-800 shrink-0"}>
                        {done ? <><Check className="w-4 h-4 mr-1" /> Added</> : <><Plus className="w-4 h-4 mr-1" /> Fix this</>}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(data.coverage_limitations || []).length > 0 && (
        <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="reputation-coverage">
          <p className="text-[11px] font-semibold text-slate-600 flex items-center gap-1.5 mb-1"><Info className="w-3.5 h-3.5" /> Data coverage limitations</p>
          <ul className="list-disc list-inside text-[11px] text-slate-500 space-y-0.5">
            {data.coverage_limitations.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
        <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-slate-500">{data.disclaimer}</p>
      </div>
    </div>
  );
}

export default ReputationIntelligence;
